import { useState } from 'react';
import { Button } from '@bb/ui';
import type { FilterCatalog } from '../../api/client.ts';
import { FilterPicker } from './FilterPicker.tsx';
import { FilterRow } from './FilterRow.tsx';
import { moveChild, nodeId, removeChild, type FilterLeaf, type FilterNode, type GroupNode } from './tree-edit.ts';

interface Props {
  group: GroupNode;
  /** Shown name: the group's label or "Group N". */
  title: string;
  readOnly: boolean;
  catalog: FilterCatalog;
  environmentId: string;
  warnings: ReadonlyArray<{ nodeId: string; message: string }>;
  onChange: (group: GroupNode) => void;
  onRemove?: () => void;
  onMove?: (delta: number) => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
}

/**
 * A filter group (BB-40): AND/OR between its conditions, a name, its own warnings. Nested groups from the API are
 * edited the same way, so the UI never hides part of a stored tree.
 */
export function FilterGroup({ group, title, readOnly, catalog, environmentId, warnings, onChange, onRemove, onMove, canMoveUp, canMoveDown }: Props) {
  const [picking, setPicking] = useState(false);
  const mine = warnings.filter((w) => w.nodeId === group.id);
  const setChild = (index: number, child: FilterNode) => onChange({ ...group, children: group.children.map((c, i) => (i === index ? child : c)) });

  return (
    <fieldset className="audience-group" aria-label={title}>
      <legend>
        {readOnly ? (
          title
        ) : (
          <input aria-label={`${title}: name`} value={group.label ?? ''} placeholder={title} onChange={(e) => onChange({ ...group, label: e.target.value || undefined })} />
        )}
      </legend>
      <div role="radiogroup" aria-label={`${title}: match`} className="audience-group__operator">
        {(['and', 'or'] as const).map((kind) => (
          <label key={kind}>
            <input type="radio" name={`${group.id}-op`} checked={group.kind === kind} disabled={readOnly} onChange={() => onChange({ ...group, kind })} /> {kind === 'and' ? 'All conditions (AND)' : 'Any condition (OR)'}
          </label>
        ))}
      </div>
      {mine.map((w) => (
        <p key={w.message} role="status" className="notice notice--warning">
          {w.message}
        </p>
      ))}
      <ol className="audience-group__filters">
        {group.children.map((child, index) =>
          child.kind === 'filter' ? (
            <FilterRow
              key={child.id}
              node={child}
              index={index}
              count={group.children.length}
              readOnly={readOnly}
              catalog={catalog}
              environmentId={environmentId}
              onChange={(node: FilterLeaf) => setChild(index, node)}
              onRemove={() => onChange(removeChild(group, child.id))}
              onMove={(delta) => onChange(moveChild(group, index, delta))}
            />
          ) : child.kind === 'not' ? (
            <li key={child.id}>NOT (nested condition, edit through the API)</li>
          ) : (
            <li key={child.id}>
              <FilterGroup
                group={child}
                title={child.label ?? `${title}.${index + 1}`}
                readOnly={readOnly}
                catalog={catalog}
                environmentId={environmentId}
                warnings={warnings}
                onChange={(next) => setChild(index, next)}
                onRemove={() => onChange(removeChild(group, child.id))}
              />
            </li>
          ),
        )}
      </ol>
      {!readOnly && (
        <div className="filters">
          <Button size="sm" onClick={() => setPicking(true)}>
            Add filter to {title}
          </Button>
          {picking && (
            <FilterPicker
              open
              onOpenChange={setPicking}
              catalog={catalog}
              onPick={(entry) => onChange({ ...group, children: [...group.children, { kind: 'filter', id: nodeId('f'), filter: entry.template as never }] })}
            />
          )}
          {onMove && (
            <>
              <Button size="sm" variant="ghost" onClick={() => onMove(-1)} disabled={!canMoveUp} aria-label={`Move ${title} up`}>
                ↑
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onMove(1)} disabled={!canMoveDown} aria-label={`Move ${title} down`}>
                ↓
              </Button>
            </>
          )}
          {onRemove && (
            <Button size="sm" variant="danger" onClick={onRemove}>
              Remove {title}
            </Button>
          )}
        </div>
      )}
    </fieldset>
  );
}
